import { announce } from '@theme/component';

function durationInMilliseconds(value, fallback) {
  const normalizedValue = value.trim();
  const duration = Number.parseFloat(normalizedValue);
  if (!Number.isFinite(duration)) return fallback;
  return normalizedValue.endsWith('s') && !normalizedValue.endsWith('ms') ? duration * 1000 : duration;
}

function longestTransitionDuration(element) {
  if (!(element instanceof Element)) return 0;

  const styles = window.getComputedStyle(element);
  const durations = styles.transitionDuration.split(',');
  const delays = styles.transitionDelay.split(',');

  return durations.reduce((longestDuration, duration, index) => {
    const delay = delays[index % delays.length] || '0s';
    const totalDuration =
      durationInMilliseconds(duration, 0) + durationInMilliseconds(delay, 0);
    return Math.max(longestDuration, totalDuration);
  }, 0);
}

/**
 * Opening, dismissal, focus, the scroll lock, and `aria-expanded` belong to the global dialog runtime in `assets/dialog.js`.
 */
class DieuxIngredientsDialog extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;

    this.controller = new AbortController();
    this.dialog = this.querySelector('[data-ingredients-dialog]');
    if (!(this.dialog instanceof HTMLDialogElement)) return;

    this.scroller = this.querySelector('[data-ingredients-scroller]');
    this.dialogBody = this.querySelector('.ingredients-dialog__body');
    this.detail = this.querySelector('[data-ingredient-detail]');
    this.detailContent = this.querySelector('[data-ingredient-detail-content]');
    this.detailTitle = this.querySelector('[data-ingredient-detail-title]');
    this.detailCopy = this.querySelector('[data-ingredient-detail-copy]');
    this.detailCount = this.querySelector('[data-ingredient-detail-count]');
    this.status = this.querySelector('[data-ingredient-status]');
    this.desktopDialogQuery = window.matchMedia('(min-width: 62rem)');
    this.reducedMotion = (window.DieuxFrame?.motionQuery ?? window.matchMedia('(prefers-reduced-motion: reduce)'));
    this.currentVariantId = this.dataset.initialVariantId || '';
    this.activeIngredientIndex = -1;
    this.animations = new Set();
    this.transitionVersion = 0;
    this.detailResetTimer = 0;
    this.detailResetHandler = undefined;
    this.detailScrollTimer = 0;
    this.detailScrollHandler = undefined;

    const styles = window.getComputedStyle(this.dialog);
    this.fastDuration = durationInMilliseconds(styles.getPropertyValue('--motion-duration-fast'), 125);
    this.standardDuration = durationInMilliseconds(
      styles.getPropertyValue('--motion-duration-standard'),
      200
    );
    this.motionEasing =
      styles.getPropertyValue('--motion-easing').trim() ||
      'ease-in-out';

    this.addEventListener('click', (event) => this.handleClick(event), {
      signal: this.controller.signal,
    });
    this.addEventListener('keydown', (event) => this.handleKeydown(event), {
      signal: this.controller.signal,
    });
    this.dialog.addEventListener('dieux:dialog:open', () => this.handleDialogOpen(), {
      signal: this.controller.signal,
    });
    this.dialog.addEventListener('close', () => this.scheduleDetailsResetAfterClose(), {
      signal: this.controller.signal,
    });
    if (this.dataset.followVariantChanges !== 'false') {
      document.addEventListener(
        'dieux:product-variant-change',
        (event) => this.handleVariantChange(event),
        { signal: this.controller.signal }
      );
    }

    this.showVariant(this.currentVariantId);
  }

  disconnectedCallback() {
    this.close({ restoreFocus: false });
    this.clearPendingDetailReset();
    this.clearPendingDetailScroll();
    this.closeDetails();
    this.controller?.abort();
    this.cancelAnimations();
    this.controller = undefined;
  }

  get currentPanel() {
    return this.querySelector(`[data-ingredient-variant="${CSS.escape(String(this.currentVariantId))}"]`);
  }

  get ingredientButtons() {
    return this.currentPanel ? [...this.currentPanel.querySelectorAll('[data-ingredient-button]')] : [];
  }

  /**
   * A close cleanup that was still pending when the popup reopens would bail out
   * and never run, so the detail view is reset here instead.
   */
  handleDialogOpen() {
    this.clearPendingDetailReset();
    this.clearPendingDetailScroll();
    this.closeDetails();
  }

  close({ restoreFocus = true } = {}) {
    this.clearPendingDetailScroll();
    if (this.dialog?.open) window.DieuxDialog?.closeDialog(this.dialog, { restoreFocus });
  }

  clearPendingDetailReset() {
    window.clearTimeout(this.detailResetTimer);
    this.detailResetTimer = 0;

    if (this.detailResetHandler && this.dialog) {
      this.dialog.removeEventListener('transitionend', this.detailResetHandler);
    }

    this.detailResetHandler = undefined;
  }

  resetDetailsAfterClose() {
    this.clearPendingDetailReset();
    if (this.dialog?.open) return;
    this.closeDetails();
  }

  scheduleDetailsResetAfterClose() {
    this.clearPendingDetailReset();
    const transitionDuration = longestTransitionDuration(this.dialog);

    if (transitionDuration <= 0) {
      this.closeDetails();
      return;
    }

    this.detailResetHandler = (event) => {
      if (event.target !== this.dialog || event.propertyName !== 'opacity') return;
      this.resetDetailsAfterClose();
    };
    this.dialog.addEventListener('transitionend', this.detailResetHandler);
    this.detailResetTimer = window.setTimeout(
      () => this.resetDetailsAfterClose(),
      transitionDuration + 50
    );
  }

  clearPendingDetailScroll() {
    window.clearTimeout(this.detailScrollTimer);
    this.detailScrollTimer = 0;

    if (this.detailScrollHandler && this.dialogBody) {
      this.dialogBody.removeEventListener('transitionend', this.detailScrollHandler);
    }

    this.detailScrollHandler = undefined;
  }

  scrollToMobileDetail() {
    this.clearPendingDetailScroll();
    if (
      !this.dialog?.open ||
      !this.scroller ||
      this.desktopDialogQuery.matches ||
      this.activeIngredientIndex < 0
    ) {
      return;
    }

    const maximumScrollTop = Math.max(0, this.scroller.scrollHeight - this.scroller.clientHeight);
    if (maximumScrollTop <= 1) return;

    this.scroller.scrollTo({
      top: maximumScrollTop,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  scheduleMobileDetailScroll() {
    this.clearPendingDetailScroll();
    if (!this.dialog?.open || this.desktopDialogQuery.matches) return;

    const transitionDuration = longestTransitionDuration(this.dialogBody);
    if (transitionDuration <= 0) {
      this.detailScrollTimer = window.setTimeout(() => this.scrollToMobileDetail());
      return;
    }

    this.detailScrollHandler = (event) => {
      if (event.target !== this.dialogBody || event.propertyName !== 'grid-template-rows') return;
      this.scrollToMobileDetail();
    };
    this.dialogBody.addEventListener('transitionend', this.detailScrollHandler);
    this.detailScrollTimer = window.setTimeout(
      () => this.scrollToMobileDetail(),
      transitionDuration + 50
    );
  }

  handleVariantChange(event) {
    if (String(event.detail?.productId) !== String(this.dataset.productId)) return;
    const variantId = event.detail?.variant?.id;
    this.currentVariantId = variantId ? String(variantId) : '';
    this.showVariant(this.currentVariantId);
  }

  showVariant(variantId) {
    this.querySelectorAll('[data-ingredient-variant]').forEach((panel) => {
      panel.hidden = panel.dataset.ingredientVariant !== String(variantId);
    });
    this.closeDetails();

    if (!this.dialog?.id) return;
    const hasPanel = Boolean(this.currentPanel);
    document.querySelectorAll(`button[aria-controls="${CSS.escape(this.dialog.id)}"]`).forEach((button) => {
      const trigger = button.closest('dieux-ingredients-dialog-trigger') || button;
      trigger.hidden = !hasPanel;
    });

    if (!hasPanel && this.dialog.open) this.close();
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) return;

    const detailControl = event.target.closest('[data-ingredient-detail-action]');
    if (detailControl && this.activeIngredientIndex >= 0) {
      const buttons = this.ingredientButtons;
      if (buttons.length === 0) return;
      const direction = detailControl.dataset.ingredientDetailAction === 'previous' ? -1 : 1;
      const nextIndex = (this.activeIngredientIndex + direction + buttons.length) % buttons.length;
      this.showDetails(nextIndex);
      return;
    }

    const ingredientButton = event.target.closest('[data-ingredient-button]');
    if (!ingredientButton || !this.currentPanel?.contains(ingredientButton)) return;
    event.preventDefault();
    const fromKeyboard = !(event instanceof MouseEvent && event.detail > 0);
    this.showDetails(this.ingredientButtons.indexOf(ingredientButton), {
      scrollOnOpen: true,
      focusDetail: fromKeyboard,
    });
  }

  handleKeydown(event) {
    if (!(event.target instanceof Element)) return;
    if (event.key === 'Escape') {
      this.closeDetailsToTrigger(event);
      return;
    }
    if (event.key !== ' ') return;
    const ingredientButton = event.target.closest('[data-ingredient-button]');
    if (!(ingredientButton instanceof HTMLElement) || !this.currentPanel?.contains(ingredientButton)) return;
    event.preventDefault();
    if (!event.repeat) ingredientButton.click();
  }

  /**
   * Closes the open detail view from inside it and returns focus to the ingredient it shows.
   *
   * @param {KeyboardEvent} event - Escape keydown from inside the dialog.
   * @returns {void}
   */
  closeDetailsToTrigger(event) {
    if (this.activeIngredientIndex < 0 || !this.detail?.contains(event.target)) return;
    const trigger = this.ingredientButtons[this.activeIngredientIndex];
    if (!(trigger instanceof HTMLElement)) return;
    event.preventDefault();
    event.stopPropagation();
    trigger.focus();
    this.closeDetails();
  }

  updateIngredientState(index) {
    this.querySelectorAll('li[aria-owns]').forEach((item) => item.removeAttribute('aria-owns'));
    this.ingredientButtons.forEach((button, buttonIndex) => {
      const isActive = buttonIndex === index;
      button.setAttribute('aria-expanded', String(isActive));
      if (isActive && this.detail?.id) button.closest('li')?.setAttribute('aria-owns', this.detail.id);
    });
  }

  /**
   * Builds the live-region message for the ingredient now shown in the detail view.
   *
   * @param {number} index - Zero-based ingredient position.
   * @param {number} count - Ingredients in the current list.
   * @param {string} title - Ingredient name.
   * @param {HTMLElement} body - The detail copy just rendered for it.
   * @returns {string}
   */
  detailStatus(index, count, title, body) {
    const template = this.status?.dataset.statusTemplate || '__INDEX__/__COUNT__: __TITLE__';
    const heading = template
      .replace('__INDEX__', String(index + 1))
      .replace('__COUNT__', String(count))
      .replace('__TITLE__', () => title);
    const copy = [...body.querySelectorAll('.ingredients-dialog__definition, .ingredients-dialog__benefits')]
      .map((element) => element.textContent.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .join(' ');
    return copy ? `${heading}. ${copy}` : heading;
  }

  closeDetails() {
    this.clearPendingDetailScroll();
    this.transitionVersion += 1;
    this.cancelAnimations();
    this.activeIngredientIndex = -1;
    this.updateIngredientState(-1);
    this.dialog?.removeAttribute('data-detail-open');
    this.detail?.setAttribute('aria-hidden', 'true');
    if (this.detail instanceof HTMLElement) this.detail.inert = true;
    if (this.detailCopy) this.detailCopy.replaceChildren();
    if (this.detailTitle) this.detailTitle.textContent = '';
    if (this.detailCount) this.detailCount.textContent = '';
    if (this.status) this.status.textContent = '';
  }

  async showDetails(index, { scrollOnOpen = false, focusDetail = false } = {}) {
    const buttons = this.ingredientButtons;
    const trigger = buttons[index];
    if (!(trigger instanceof HTMLElement)) return;

    if (index === this.activeIngredientIndex) {
      this.closeDetails();
      return;
    }

    const transitionVersion = ++this.transitionVersion;
    const hadActiveIngredient = this.activeIngredientIndex >= 0;
    const outgoingElement = hadActiveIngredient ? this.detailCopy?.firstElementChild : undefined;
    const previousHeight =
      hadActiveIngredient && this.detailCopy
        ? this.detailCopy.getBoundingClientRect().height
        : 0;
    const outgoingOpacity =
      outgoingElement instanceof HTMLElement
        ? Number.parseFloat(window.getComputedStyle(outgoingElement).opacity)
        : 1;
    const outgoingSnapshot =
      outgoingElement instanceof HTMLElement
        ? outgoingElement.cloneNode(true)
        : undefined;

    this.cancelAnimations();
    this.activeIngredientIndex = index;
    this.updateIngredientState(index);

    const template = document.getElementById(trigger.dataset.ingredientTemplate || '');
    const incomingContent =
      template instanceof HTMLTemplateElement
        ? template.content.cloneNode(true)
        : document.createDocumentFragment();
    const incomingWrapper = document.createElement('div');
    incomingWrapper.className = 'ingredients-dialog__detail-body';
    incomingWrapper.append(incomingContent);

    if (this.detailCopy) {
      this.detailCopy.replaceChildren(incomingWrapper);
      if (outgoingSnapshot instanceof HTMLElement) {
        outgoingSnapshot.style.opacity = String(
          Number.isFinite(outgoingOpacity) ? outgoingOpacity : 1
        );
        outgoingSnapshot.setAttribute('data-ingredient-detail-snapshot', '');
        outgoingSnapshot.setAttribute('aria-hidden', 'true');
        outgoingSnapshot.inert = true;
        this.detailCopy.append(outgoingSnapshot);
      }
    }

    if (this.detailTitle) this.detailTitle.textContent = trigger.dataset.ingredientTitle || '';
    if (this.detailCount) this.detailCount.textContent = `${index + 1}/${buttons.length}`;
    this.dialog?.setAttribute('data-detail-open', 'true');
    this.detail?.setAttribute('aria-hidden', 'false');
    if (this.detail instanceof HTMLElement) this.detail.inert = false;
    if (focusDetail) window.DieuxAnchorScroll?.focusLanding?.(this.detail);
    announce(
      this.status,
      this.detailStatus(index, buttons.length, trigger.dataset.ingredientTitle || '', incomingWrapper)
    );
    if (!hadActiveIngredient && scrollOnOpen) this.scheduleMobileDetailScroll();

    const nextHeight =
      incomingWrapper instanceof HTMLElement
        ? incomingWrapper.getBoundingClientRect().height
        : 0;

    if (this.reducedMotion.matches || typeof incomingWrapper.animate !== 'function') {
      outgoingSnapshot?.remove();
      return;
    }

    const animations = [
      ...this.animateOut(outgoingSnapshot),
      ...this.animateIn(incomingWrapper),
      ...this.animateHeight(this.detailCopy, previousHeight, nextHeight, hadActiveIngredient),
    ];
    await Promise.all(animations.map((animation) => animation.finished.catch(() => {})));

    animations.forEach((animation) => {
      this.animations.delete(animation);
      animation.cancel();
    });
    if (transitionVersion === this.transitionVersion) {
      outgoingSnapshot?.remove();
      this.detailCopy?.removeAttribute('data-ingredient-detail-resizing');
    }
  }

  animateIn(element) {
    if (!(element instanceof HTMLElement)) return [];

    return [
      this.trackAnimation(
        element.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: this.fastDuration,
          easing: this.motionEasing,
          fill: 'both',
        })
      ),
    ];
  }

  animateOut(element) {
    if (!(element instanceof HTMLElement) || typeof element.animate !== 'function') return [];

    const renderedOpacity = Number.parseFloat(window.getComputedStyle(element).opacity);
    return [
      this.trackAnimation(
        element.animate(
          [
            { opacity: Number.isFinite(renderedOpacity) ? renderedOpacity : 1 },
            { opacity: 0 },
          ],
          {
            duration: this.fastDuration,
            easing: this.motionEasing,
            fill: 'both',
          }
        )
      ),
    ];
  }

  animateHeight(element, previousHeight, nextHeight, shouldAnimate) {
    if (
      !shouldAnimate ||
      !(element instanceof HTMLElement) ||
      typeof element.animate !== 'function' ||
      !Number.isFinite(previousHeight) ||
      !Number.isFinite(nextHeight) ||
      Math.abs(previousHeight - nextHeight) < 1
    ) {
      return [];
    }

    element.setAttribute('data-ingredient-detail-resizing', '');
    return [
      this.trackAnimation(
        element.animate(
          [
            { blockSize: `${previousHeight}px` },
            { blockSize: `${nextHeight}px` },
          ],
          {
            duration: this.standardDuration,
            easing: this.motionEasing,
            fill: 'both',
          }
        )
      ),
    ];
  }

  trackAnimation(animation) {
    this.animations.add(animation);
    return animation;
  }

  cancelAnimations() {
    this.animations.forEach((animation) => animation.cancel());
    this.animations.clear();
    this.detailCopy?.removeAttribute('data-ingredient-detail-resizing');
  }
}

if (!customElements.get('dieux-ingredients-dialog')) {
  customElements.define('dieux-ingredients-dialog', DieuxIngredientsDialog);
}
